import { z } from 'zod';
import { OffsetPaginationParamsSchema } from '../offset-pagination.js';
// ---------------------------------------------------------------------------
// Kit catalogue — a kit type used within ONE laboratory of one trial (domain 5,
// REQ-KIT-003…006; legacy `Lab_kit`, dbase [s.24]) and its expiry-date batches
// (legacy `Lab_kit_expiration`, dbase [s.25]).
//
// Icons (REQ-KIT-003: `Icon_global` / `Icon_id`) depend on the Domain 2 icon
// base (REQ-TRIAL-015) which does not exist yet — deliberately out of scope
// (user decision 2026-09-27), so the DTO carries no icon field.
//
// `expiration` has NO default on purpose (REQ-KIT-004): the classification
// "with expiry date" / "open-ended" must be taken consciously when the kit is
// created, so a POST without it is a 400, never a silent default.
//
// The "classification before the FIRST shipment" part of REQ-KIT-004 needs a
// shipment to block against — it lands together with the shipment atom, in the
// single named place marked in `kits.service.ts`.
// ---------------------------------------------------------------------------
export const LabKitDtoSchema = z.object({
    id: z.number().int().positive(),
    tenantId: z.number().int().positive(),
    trialLaboratoryId: z.number().int().positive(),
    name: z.string().min(1).max(255),
    description: z.string().nullable(),
    notes: z.string().nullable(),
    expiration: z.boolean(),
    localisation: z.string().nullable(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
});
export const CreateLabKitRequestSchema = z.object({
    trialLaboratoryId: z.number().int().positive(),
    name: z.string().min(1, 'Nazwa kitu jest wymagana').max(255),
    description: z.string().max(10_000).optional(),
    notes: z.string().max(10_000).optional(),
    // REQ-KIT-004: required, no default — the classification must be chosen, not inherited.
    expiration: z.boolean({ error: 'Klasyfikacja kitu (z datą przydatności / bezterminowy) jest wymagana' }),
    localisation: z.string().max(10_000).optional(),
});
/**
 * `strict()` on purpose, exactly like the laboratory update: a kit NEVER moves
 * to another laboratory (its batches, stock and FEFO history belong to one lab
 * of one trial), and its `expiration` classification is not re-decided through
 * a PATCH — a silently stripped field would look like a successful move in the
 * UI. The service enforces this again with a friendly message for the two
 * fields that are most likely to be sent by mistake.
 */
export const UpdateLabKitRequestSchema = z
    .object({
    name: z.string().min(1, 'Nazwa kitu jest wymagana').max(255).optional(),
    description: z.string().max(10_000).nullable().optional(),
    notes: z.string().max(10_000).nullable().optional(),
    localisation: z.string().max(10_000).nullable().optional(),
})
    .strict();
export const LabKitListQuerySchema = OffsetPaginationParamsSchema.extend({
    /** Narrow the catalogue to one laboratory of a trial (optional). */
    trialLaboratoryId: z.coerce.number().int().positive().optional(),
    search: z.string().max(100).optional(),
});
export const LabKitExpirationDtoSchema = z.object({
    id: z.number().int().positive(),
    tenantId: z.number().int().positive(),
    labKitId: z.number().int().positive(),
    /** ISO calendar date (`YYYY-MM-DD`); the service expands a month-only input. */
    expirationDate: z.iso.date(),
    batchComment: z.string().nullable(),
    createdAt: z.iso.datetime(),
});
export const CreateLabKitExpirationRequestSchema = z.object({
    /**
     * REQ-KIT-005: the packaging often shows month + year only (Nov-2022), so a
     * month-only input is accepted here and normalised to the LAST day of that
     * month by the service. A full date is taken as given.
     */
    expirationDate: z.union([
        z.iso.date(),
        z
            .string()
            .regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'Podaj datę w formacie RRRR-MM-DD albo sam miesiąc jako RRRR-MM'),
    ]),
    /** REQ-KIT-005: free comment describing the batch (e.g. damaged packaging). */
    batchComment: z.string().max(10_000).optional(),
});
//# sourceMappingURL=kits.schema.js.map